/**
 * **Jockey — the rider attachment's rules core** (RAW Core p.75). Pure rules (no I/O, no dice):
 * the partner-lookup helpers, the legality gates the
 * gameplay-tier intents run before resolving, and the grounded step builders for attaching,
 * detaching, and the Distract menu option. The room (`backend/.../encounterRoom.ts`) calls
 * these; `resolveContest` (`contest.ts`) supplies the attach/shake-off opposed-check math, and
 * `resolveHeatCascade`/`resolveDamageCascade` (`tables.ts`) supply Shred/Damage's cascades.
 *
 * **Asymmetric partner pair, unlike Grapple.** Grapple's `Grappled` marker is the SAME status
 * name on both parties; Jockey's rider and carrier play different roles, so
 * they carry DIFFERENT marker names — `Jockeying` on the rider, `Jockeyed` on the carrier —
 * each `partner`-stamped at the other (the same `partner?: CombatantId` field Grapple's
 * `StatusInstance` extension added, reused verbatim, no new domain surface).
 *
 * **The shared-space attach is a position ASSIGNMENT, never a `move_token`.**
 * {@link jockeyAttachSteps} records a plain `move` step (self-inverting, undoable) that plants
 * the rider directly on the carrier's cell — RAW "climb onto the mech, sharing its space." This
 * mirrors `pilotBridge.ts`'s Dismount/Eject placement (`add_combatant` at a computed cell, never
 * a follow-up `move_token`) and is why the post-move recomputer that keeps them together
 * (`jockeyRecomputer`, `spatialRecompute.ts`) never routes through `move_token` either — the
 * stop/occupancy classification never evaluates the shared-space entry, by construction.
 */

import { type Cell, type CombatantId, type EncounterState, type ResolvedStep, hasStatus, sideOf, SLOWED_STATUS } from "@lancer-vtt/domain";
import { type Result, ok, err } from "@lancer-vtt/shared";
import { combatantCellRange, combatantRange } from "./effects/spatialAnswerer";
import { jammedCockpitRefusal, pilotFootprintFits } from "./pilotBridge";

/** The rider-side marker `act_jockey`'s successful attach lands (RAW Core p.75). */
export const JOCKEYING_STATUS = "Jockeying";
/** The carrier-side marker `act_jockey`'s successful attach lands (RAW Core p.75). */
export const JOCKEYED_STATUS = "Jockeyed";

/**
 * The rider-side marker a consent-based allied MOUNT lands (RAW Core p.70) — this module's
 * own local copy of the identical-string domain const
 * ({@link "@lancer-vtt/domain".RIDING_STATUS}), mirroring
 * the two-copy precedent this file already keeps for {@link JOCKEYING_STATUS}/{@link
 * JOCKEYED_STATUS} (the domain layer needs its own copy for `classifyOccupant`, below
 * `rules` in the dependency graph; this rules-layer copy is for local consumers — partner
 * lookups, legality gates, step builders).
 */
export const RIDING_STATUS = "Riding";
/** The carrier-side marker a consent-based allied MOUNT lands — see {@link RIDING_STATUS}'s doc. */
export const RIDDEN_STATUS = "Ridden";

/** The live `Jockeying`/`Jockeyed`/`Riding`/`Ridden` marker on `actor`, if any. */
function jockeyMarker(state: EncounterState, actor: string, status: string) {
  return state.combatants[actor]?.statuses.find((s) => s.name === status);
}

/** The carrier `pilotId` is riding, read off its own `Jockeying` marker's partner-ref. */
export function jockeyPartner(state: EncounterState, pilotId: string): CombatantId | undefined {
  return jockeyMarker(state, pilotId, JOCKEYING_STATUS)?.partner;
}

/** The rider currently jockeying `mechId`, read off its own `Jockeyed` marker's partner-ref. */
export function jockeyedRiderOf(state: EncounterState, mechId: string): CombatantId | undefined {
  return jockeyMarker(state, mechId, JOCKEYED_STATUS)?.partner;
}

/**
 * The carrier `pilotId` is consensually riding, read off its own `Riding` marker's partner-ref
 * — the {@link RIDING_STATUS} sibling of {@link jockeyPartner}.
 */
export function ridePartner(state: EncounterState, pilotId: string): CombatantId | undefined {
  return jockeyMarker(state, pilotId, RIDING_STATUS)?.partner;
}

/** A Jockey-family legality verdict — `Result<void>`, mirroring `grapple.ts`'s `BridgeVerdict`-style shape. */
export type JockeyVerdict = Result<void>;

/**
 * May `pilotId` `request_jockey` against `mechId` (RAW Core p.75, `act_jockey`, a FULL action)?
 * Checks, in order: both exist and are the right kind; the target is mech-scale (`kind`
 * `"mech"` OR `"npc"` — this engine's two-team model means an ENEMY mech is always represented
 * as `kind: "npc"` (`sideOf`: `mech`/`pilot` ⇒ `"pc"`, else `"npc"` — the same team split
 * `isEngagedWith` uses; a `kind: "mech"` combatant is always a friendly PC mech and so can
 * never pass the hostility check below — that is Grapple/Ram's world too, not a bug); the
 * target is hostile (`sideOf` mismatch — RAW's own "an enemy mech" wording); the target isn't
 * already Destroyed; neither party is already jockeying/jockeyed (one rider per mech, mirroring
 * Grapple's "one grapple per combatant" limitation); both are
 * placed; and they are exactly adjacent (distance 1 — "you must be adjacent to a mech").
 */
export function checkJockeyLegality(state: EncounterState, pilotId: CombatantId, mechId: CombatantId): JockeyVerdict {
  const pilot = state.combatants[pilotId];
  if (!pilot) return err(`jockey: no such combatant ${pilotId}`);
  if (pilot.kind !== "pilot") return err(`jockey: ${pilot.name} is not a pilot`);
  const mech = state.combatants[mechId];
  if (!mech) return err(`jockey: no such combatant ${mechId}`);
  if (mech.kind !== "mech" && mech.kind !== "npc") return err(`jockey: ${mech.name} is not a mech`);
  if (sideOf(pilot) === sideOf(mech)) return err(`jockey: ${mech.name} is not an enemy mech`);
  if (hasStatus(mech.statuses, "Destroyed")) return err(`jockey: ${mech.name} is destroyed`);
  if (hasStatus(pilot.statuses, JOCKEYING_STATUS)) return err(`jockey: ${pilot.name} is already jockeying a mech`);
  if (hasStatus(mech.statuses, JOCKEYED_STATUS)) return err(`jockey: ${mech.name} already has a rider`);
  if (!pilot.pos) return err(`jockey: ${pilot.name} isn't placed on the map`);
  if (!mech.pos) return err(`jockey: ${mech.name} isn't placed on the map`);
  const dist = combatantRange(state, pilotId, mechId);
  if (dist !== 1) return err(`jockey: ${pilot.name} must be adjacent to ${mech.name} (got distance ${dist ?? "unreachable"})`);
  return ok(undefined);
}

/**
 * May `pilotId` `request_ride` an allied MOUNT onto `mechId` (RAW Core p.70, `act_ride`, a
 * FULL action)? Structurally
 * {@link checkJockeyLegality} with the side test INVERTED and the carrier kind narrowed:
 * checks, in order: both exist and are the right kind — rider `kind === "pilot"`, carrier
 * `kind === "mech"` **only** (unlike Jockey, `"npc"` is dropped — a same-side carrier is
 * never represented as `kind: "npc"` in this engine's two-team model, `sideOf`'s own doc;
 * **this `kind === "mech"`/`"pilot"` read is a tagged migrates-with-Jockey kind-read** — it
 * matches Jockey's own predicate vocabulary at this seam rather than standing up a lone
 * identity predicate for one caller); the target is a **willing ally** — `sideOf` MATCH
 * required (the exact inverse of Jockey's hostility mismatch — "willing" is modeled as
 * same-side, no consent prompt); the target isn't already Destroyed; neither party
 * already carries a co-location marker (rider must not already be `Riding` OR `Jockeying`,
 * carrier must not already be `Ridden` — no stacking; one rider per mech, mirroring
 * Jockey's own limit); both are placed; and they are exactly adjacent (distance 1 — "you
 * must be adjacent your mech").
 */
export function checkRideLegality(state: EncounterState, pilotId: CombatantId, mechId: CombatantId): JockeyVerdict {
  const pilot = state.combatants[pilotId];
  if (!pilot) return err(`ride: no such combatant ${pilotId}`);
  if (pilot.kind !== "pilot") return err(`ride: ${pilot.name} is not a pilot`);
  const mech = state.combatants[mechId];
  if (!mech) return err(`ride: no such combatant ${mechId}`);
  if (mech.kind !== "mech") return err(`ride: ${mech.name} is not a mech`);
  if (sideOf(pilot) !== sideOf(mech)) return err(`ride: ${mech.name} is not a willing ally`);
  if (hasStatus(mech.statuses, "Destroyed")) return err(`ride: ${mech.name} is destroyed`);
  const jammed = jammedCockpitRefusal(mech, "ride");
  if (jammed) return err(jammed);
  if (hasStatus(pilot.statuses, RIDING_STATUS)) return err(`ride: ${pilot.name} is already riding a mech`);
  if (hasStatus(pilot.statuses, JOCKEYING_STATUS)) return err(`ride: ${pilot.name} is already jockeying a mech`);
  if (hasStatus(mech.statuses, RIDDEN_STATUS)) return err(`ride: ${mech.name} already has a rider`);
  if (!pilot.pos) return err(`ride: ${pilot.name} isn't placed on the map`);
  if (!mech.pos) return err(`ride: ${mech.name} isn't placed on the map`);
  const dist = combatantRange(state, pilotId, mechId);
  if (dist !== 1) return err(`ride: ${pilot.name} must be adjacent to ${mech.name} (got distance ${dist ?? "unreachable"})`);
  return ok(undefined);
}

/**
 * May `riderId` (an on-foot pilot currently `Riding` a willing ally) DISMOUNT to `at` (RAW
 * Core p.70, `act_ride_dismount`, a FULL action)? Checks, in
 * order: `riderId` exists and is a pilot; it carries a live `Riding` marker with a resolvable
 * partner (its carrier); the carrier is placed on the map; `at` is exactly adjacent to the
 * carrier's cell (distance 1 — the mirror of `checkDismountLegality`'s own adjacency-to-mech
 * check, here measured from the CARRIER since that's where the rider's shared cell actually
 * is); and `at` has a free Size-0.5 footprint ({@link pilotFootprintFits}, the SAME gate the
 * mech-bridge Dismount/Eject use — RAW: "if there are no free spaces, you cannot DISMOUNT").
 * Doesn't re-check `riderId`'s own placement (a live `Riding` marker implies it's co-located
 * with its carrier, hence placed) or the carrier's kind/side (the pairing was already
 * legality-gated at attach time by {@link checkRideLegality}; a live pair is trusted here,
 * mirroring {@link checkJockeyPairLegality}'s own trust-the-pair-once-attached discipline).
 */
export function checkRideDismountLegality(state: EncounterState, riderId: CombatantId, at: Cell): JockeyVerdict {
  const rider = state.combatants[riderId];
  if (!rider) return err(`ride-dismount: no such combatant ${riderId}`);
  if (rider.kind !== "pilot") return err(`ride-dismount: ${rider.name} is not a pilot`);
  const mechId = ridePartner(state, riderId);
  if (mechId === undefined) return err(`ride-dismount: ${rider.name} is not currently riding a mech`);
  const mech = state.combatants[mechId];
  if (!mech) return err(`ride-dismount: ${rider.name}'s Riding marker has no resolvable carrier`);
  const jammed = jammedCockpitRefusal(mech, "ride-dismount");
  if (jammed) return err(jammed);
  if (!mech.pos) return err(`ride-dismount: ${mech.name} isn't placed on the map`);
  const dist = combatantCellRange(state, mechId, at);
  if (dist !== 1) return err(`ride-dismount: target must be an adjacent free space (got distance ${dist ?? "unreachable"})`);
  if (!pilotFootprintFits(state, at)) return err(`ride-dismount: no free adjacent space at that cell — cannot DISMOUNT (RAW p.70)`);
  return ok(undefined);
}

/**
 * The recorded step list a successful `request_ride` lands — the consent-riding sibling of
 * {@link jockeyAttachSteps}: paired
 * `Riding`/`Ridden` markers (partner-stamped at each other), then a plain `move` step
 * planting the rider directly on the carrier's cell — "move into the same space" (RAW). NO
 * contest wrapper — a willing ally always attaches. Assumes {@link checkRideLegality}
 * already confirmed both parties are placed and adjacent.
 */
export function rideAttachSteps(state: EncounterState, pilotId: CombatantId, mechId: CombatantId): ResolvedStep[] {
  const pilot = state.combatants[pilotId];
  const mech = state.combatants[mechId];
  if (!pilot || !mech || !pilot.pos || !mech.pos) return [];
  return [
    { op: "status_add", target: pilotId, status: RIDING_STATUS, expiry: { kind: "until_removed" }, partner: mechId },
    { op: "status_add", target: mechId, status: RIDDEN_STATUS, expiry: { kind: "until_removed" }, partner: pilotId },
    { op: "move", target: pilotId, from: pilot.pos, to: mech.pos },
  ];
}

/** Named args for {@link checkJockeyPairLegality} (`CODE_STYLE.md` max-params-3). */
type CheckJockeyPairArgs = {
  state: EncounterState;
  selfId: CombatantId;
  selfStatus: string;
  selfNoun: string;
};

/**
 * May `pilotId` take one of the repeatable Jockey menu options (Distract/Shred/Damage) or is
 * `mechId` (the carrier) allowed to attempt a shake-off? Both share the same underlying
 * precondition — a live, resolvable, non-Destroyed `Jockeying`/`Jockeyed` pair — so
 * {@link checkJockeyMenuLegality} and {@link checkShakeOffLegality} both delegate here.
 */
function checkJockeyPairLegality(args: CheckJockeyPairArgs): JockeyVerdict {
  const { state, selfId, selfStatus, selfNoun } = args;
  const self = state.combatants[selfId];
  if (!self) return err(`${selfNoun}: no such combatant ${selfId}`);
  const marker = jockeyMarker(state, selfId, selfStatus);
  if (!marker || marker.partner === undefined) return err(`${self.name} is not currently jockeying/jockeyed`);
  const partner = state.combatants[marker.partner];
  if (!partner) return err(`${self.name}'s Jockey marker has no resolvable partner`);
  if (hasStatus(partner.statuses, "Destroyed")) return err(`${partner.name} is destroyed`);
  return ok(undefined);
}

/** May `pilotId` take a Jockey menu option (Distract/Shred/Damage, RAW Core p.75)? */
export function checkJockeyMenuLegality(state: EncounterState, pilotId: CombatantId): JockeyVerdict {
  return checkJockeyPairLegality({ state, selfId: pilotId, selfStatus: JOCKEYING_STATUS, selfNoun: "jockey menu" });
}

/** May `mechId` attempt to shake off its rider (RAW Core p.75, `act_shake_off`, a FULL action)? */
export function checkShakeOffLegality(state: EncounterState, mechId: CombatantId): JockeyVerdict {
  return checkJockeyPairLegality({ state, selfId: mechId, selfStatus: JOCKEYED_STATUS, selfNoun: "shake off" });
}

/**
 * The recorded step list a successful `request_jockey` contest applies: land the paired
 * `Jockeying`/`Jockeyed` markers (partner-stamped at each other, the Grapple `partner`
 * precedent), then a plain `move` step planting the rider directly on the
 * carrier's cell — "climb onto the mech, sharing its space" (RAW). Assumes
 * {@link checkJockeyLegality} already confirmed both parties are placed and adjacent (so the
 * `move`'s `from`/`to` can never coincide).
 */
export function jockeyAttachSteps(state: EncounterState, pilotId: CombatantId, mechId: CombatantId): ResolvedStep[] {
  const pilot = state.combatants[pilotId];
  const mech = state.combatants[mechId];
  if (!pilot || !mech || !pilot.pos || !mech.pos) return [];
  return [
    { op: "status_add", target: pilotId, status: JOCKEYING_STATUS, expiry: { kind: "until_removed" }, partner: mechId },
    { op: "status_add", target: mechId, status: JOCKEYED_STATUS, expiry: { kind: "until_removed" }, partner: pilotId },
    { op: "move", target: pilotId, from: pilot.pos, to: mech.pos },
  ];
}

/** Named args for {@link colocationDetachSteps} (`CODE_STYLE.md` max-params-3). */
type ColocationDetachArgs = {
  state: EncounterState;
  riderId: CombatantId;
  riderStatus: string;
  mechId: CombatantId;
  mechStatus: string;
};

/**
 * The co-location-marker-**family**-generic detach step-builder — clear `riderStatus` off
 * `riderId` and `mechStatus` off `mechId`, idempotent per side (mirrors
 * `grappleClearSteps`), carrying each live instance's facets onto its remove step so
 * undo/redo rebuilds it byte-exact (mirrors `pilotBridge.ts`'s `mountSteps`). Shared body
 * under both {@link jockeyDetachSteps} (`Jockeying`/`Jockeyed`) and allied-riding's own
 * detach (`Riding`/`Ridden`) — a new co-location pair adds a thin named wrapper here, not a
 * second copy of this logic.
 */
function colocationDetachSteps(args: ColocationDetachArgs): ResolvedStep[] {
  const { state, riderId, riderStatus, mechId, mechStatus } = args;
  const steps: ResolvedStep[] = [];
  const riderMark = jockeyMarker(state, riderId, riderStatus);
  if (riderMark) {
    steps.push({
      op: "status_remove",
      target: riderId,
      status: riderStatus,
      ...(riderMark.expiry !== undefined ? { expiry: riderMark.expiry } : {}),
      ...(riderMark.appliedTurn !== undefined ? { appliedTurn: riderMark.appliedTurn } : {}),
      ...(riderMark.source !== undefined ? { source: riderMark.source } : {}),
      ...(riderMark.partner !== undefined ? { partner: riderMark.partner } : {}),
    });
  }
  const carrierMark = jockeyMarker(state, mechId, mechStatus);
  if (carrierMark) {
    steps.push({
      op: "status_remove",
      target: mechId,
      status: mechStatus,
      ...(carrierMark.expiry !== undefined ? { expiry: carrierMark.expiry } : {}),
      ...(carrierMark.appliedTurn !== undefined ? { appliedTurn: carrierMark.appliedTurn } : {}),
      ...(carrierMark.source !== undefined ? { source: carrierMark.source } : {}),
      ...(carrierMark.partner !== undefined ? { partner: carrierMark.partner } : {}),
    });
  }
  return steps;
}

/**
 * The recorded step list that ends a Jockey pairing: clear `Jockeying` off the rider and
 * `Jockeyed` off the carrier. Idempotent per side (mirrors `grappleClearSteps`) — used by a
 * won shake-off contest, and by {@link "./spatialRecompute".jockeyRecomputer} when the rider
 * jumps off under its own power (RAW: "you can jump off as part of your movement"). A thin
 * wrapper over the family-generic {@link colocationDetachSteps}.
 */
export function jockeyDetachSteps(state: EncounterState, riderId: CombatantId, mechId: CombatantId): ResolvedStep[] {
  return colocationDetachSteps({ state, riderId, riderStatus: JOCKEYING_STATUS, mechId, mechStatus: JOCKEYED_STATUS });
}

/**
 * The consent-riding sibling of {@link jockeyDetachSteps}: ends a `Riding`/`Ridden`
 * pairing (clear `Riding` off the rider, `Ridden` off the carrier) — same family-generic
 * body ({@link colocationDetachSteps}), the marker names alone differ. Used by the
 * generalized {@link "./spatialRecompute".jockeyRecomputer} when a consensual rider jumps
 * off under its own power, and by the explicit allied-DISMOUNT verb. Exported here rather
 * than from a new module because Jockey's plumbing is generic except the marker names.
 */
export function rideDetachSteps(state: EncounterState, riderId: CombatantId, mechId: CombatantId): ResolvedStep[] {
  return colocationDetachSteps({ state, riderId, riderStatus: RIDING_STATUS, mechId, mechStatus: RIDDEN_STATUS });
}

/**
 * The recorded step list a successful `request_ride_dismount` lands (RAW Core p.70: "when you
 * DISMOUNT, you are placed in an adjacent space") — clears
 * the `Riding`/`Ridden` pair ({@link rideDetachSteps}) THEN a plain `move` step placing the
 * rider at `at` (never `move_token` — spends no movement budget, the mirror of {@link
 * rideAttachSteps}'s own plain-assignment discipline for the outbound trip). Detach-then-move
 * is one step list so undo/redo reverses both atomically. Assumes {@link
 * checkRideDismountLegality} already confirmed the rider is `Riding` a resolvable, placed
 * carrier and `at` is a free adjacent cell.
 */
export function rideDismountSteps(state: EncounterState, riderId: CombatantId, at: Cell): ResolvedStep[] {
  const rider = state.combatants[riderId];
  const mechId = ridePartner(state, riderId);
  if (!rider || !rider.pos || mechId === undefined) return [];
  return [...rideDetachSteps(state, riderId, mechId), { op: "move", target: riderId, from: rider.pos, to: at }];
}

/**
 * The recorded step list for the **Distract** menu option (RAW Core p.75): the carrier
 * becomes Impaired and Slowed "until the end of its next turn" — `target_next_turn_end`, the
 * SAME carrier-turn-boundary expiry the domain's `StatusExpiry` union already carries (no new
 * expiry kind). `SLOWED_STATUS` is the shared domain constant; `"Impaired"` is used as a plain
 * literal, mirroring `pilotBridge.ts`'s Eject steps (no dedicated `IMPAIRED_STATUS` const exists
 * in this codebase today).
 *
 * This builds `StatusAddStep`s directly rather than through `interpret.ts`'s `add_status` atom
 * handler, so it needs its OWN `appliedTurn` stamp — the atom handler's choke-point stamp
 * doesn't reach here. In practice the Jockey menu is always the RIDER's own action (never the
 * carrier's — only one combatant is ever mid-turn at once), so the carrier can never coincide
 * with its own currently-ending turn here; stamped anyway for consistency with every other
 * `turn`-kind emitter, not because a live bug was found.
 */
export function distractSteps(state: EncounterState, mechId: CombatantId): ResolvedStep[] {
  const appliedTurn = state.activation.turnOrdinal;
  return [
    { op: "status_add", target: mechId, status: "Impaired", expiry: { kind: "turn", edge: "end", who: "target" }, appliedTurn },
    { op: "status_add", target: mechId, status: SLOWED_STATUS, expiry: { kind: "turn", edge: "end", who: "target" }, appliedTurn },
  ];
}
